import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Instagram, Youtube } from "lucide-react";

const DEFAULTS = {
  social_whatnot_url: "https://whatnot.com",
  social_tiktok_url: "https://tiktok.com",
  social_ebay_url: "https://ebay.com",
  social_instagram_url: "https://instagram.com",
  social_youtube_url: "https://youtube.com",
  contact_email: "hello@cardlobster.com",
  footer_tagline: "Catch awesome cards.",
  footer_description: "Premium singles, wholesale mystery packs, and weekly live auctions on Whatnot.",
  footer_copyright: "Lobster5 · cardlobster.com",
};

export default function Footer() {
  const [s, setS] = useState(DEFAULTS);
  useEffect(() => { api.get("/settings").then((r) => setS({ ...DEFAULTS, ...r.data })).catch(() => {}); }, []);

  return (
    <footer className="border-t border-white/5 mt-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-4">
            <img src="/brand/card-lobster-mascot.jpg" alt="Card Lobster mascot"
                 className="w-16 h-16 rounded-full ring-2 ring-[var(--cl-primary)]/40 object-cover" />
            <div className="font-display text-4xl">
              CARD<span style={{ color: "var(--cl-primary)" }}>LOBSTER</span>
            </div>
          </div>
          <p className="mt-4 text-sm text-zinc-400 max-w-md">
            <span className="text-label text-[var(--cl-orange)]">{s.footer_tagline}</span><br/>
            {s.footer_description}
          </p>
          <div className="flex gap-3 mt-6">
            <SocialBtn href={s.social_whatnot_url} testid="social-whatnot"><span className="text-[11px] font-bold">WHATNOT</span></SocialBtn>
            <SocialBtn href={s.social_tiktok_url} testid="social-tiktok"><TikTokIcon /></SocialBtn>
            <SocialBtn href={s.social_ebay_url} testid="social-ebay"><span className="text-[11px] font-bold">eBay</span></SocialBtn>
            <SocialBtn href={s.social_instagram_url} testid="social-ig"><Instagram size={16} /></SocialBtn>
            <SocialBtn href={s.social_youtube_url} testid="social-yt"><Youtube size={16} /></SocialBtn>
          </div>
        </div>
        <div>
          <div className="text-label text-zinc-500 mb-4">Shop</div>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li><Link to="/shop">Singles</Link></li>
            <li><Link to="/mystery-packs">Mystery Packs</Link></li>
            <li><Link to="/pack-contents">Pack Contents</Link></li>
            <li><Link to="/subscribe">Membership Club</Link></li>
            {s.merch_url && (
              <li>
                {s.merch_url.startsWith("http") ? (
                  <a href={s.merch_url} target="_blank" rel="noreferrer" data-testid="footer-merch">
                    {s.merch_label || "Merch"}
                  </a>
                ) : (
                  <Link to={s.merch_url} data-testid="footer-merch">
                    {s.merch_label || "Merch"}
                  </Link>
                )}
              </li>
            )}
          </ul>
        </div>
        <div>
          <div className="text-label text-zinc-500 mb-4">Company</div>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li><Link to="/live">Live Auctions</Link></li>
            <li><Link to="/big-hits">Big Hits</Link></li>
            <li><Link to="/about">About Us</Link></li>
            <li><Link to="/affiliates" data-testid="footer-affiliates">Affiliates</Link></li>
            <li><Link to="/sell-cards" data-testid="footer-sell-cards">Sell Us Your Cards</Link></li>
            <li><Link to="/account">My Account</Link></li>
            <li><a href={`mailto:${s.contact_email}`}>Contact</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row justify-between text-xs text-zinc-500">
          <span>© {new Date().getFullYear()} Card Lobster. All rights reserved.</span>
          <span>{s.footer_copyright}</span>
        </div>
      </div>
    </footer>
  );
}

function SocialBtn({ href, children, testid }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
       className="w-10 h-10 rounded-md border border-white/10 flex items-center justify-center hover:border-[var(--cl-orange)] hover:text-[var(--cl-orange)] transition-colors">
      {children}
    </a>
  );
}

function TikTokIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.6 6.7c-1.3-.8-2.1-2.2-2.1-3.8V2h-3.4v13.6c0 1.5-1.2 2.7-2.7 2.7s-2.7-1.2-2.7-2.7 1.2-2.7 2.7-2.7c.3 0 .5 0 .8.1v-3.4c-.3 0-.5-.1-.8-.1-3.3 0-6.1 2.7-6.1 6.1s2.7 6.1 6.1 6.1 6.1-2.7 6.1-6.1v-6.4c1.3.9 2.9 1.4 4.6 1.4V7c-.9 0-1.8-.1-2.5-.3z"/>
    </svg>
  );
}
